// src/components/checkout/StickerDesignStep/StickerDesignStep.jsx
//
// ШАГ 2 ОФОРМЛЕНИЯ ЗАКАЗА: «КАКИЕ СТИКЕРЫ НАПЕЧАТАТЬ?»
// (решение владельца 2026-10-29: спрашивать ЗДЕСЬ, в этой же задаче).
//
// ═══════════════════════════════════════════════════════════════════
// 🔑 ЗАЧЕМ ЭТОТ ВОПРОС ВООБЩЕ.
//    Без ответа заказ выглядит одинаково, даже если мы не знаем, что
//    печатать. Владелец: «давай сделаем макет ждём, ибо мы же не знаем,
//    что мы будем печатать». То есть заказ МОЖНО оформить, но в нём
//    должно быть видно: картинки нет и её ждут.
//
// ТРИ ВАРИАНТА — РОВНО ТЕ, ЧТО ПРЕДЛОЖИЛ ВЛАДЕЛЕЦ:
//   ① «Возьму макет из каталога» — у каждой позиции своего каталога
//      ничего спрашивать не нужно, макет уже выбран в корзине;
//   ② «Загружу свою картинку» — файл в приватную зону, привязан к
//      строке корзины, проверка прав;
//   ③ «Ещё не решил» — позиция уходит как «макет ждём».
//
// 🔴 КНОПКИ «ОФОРМИТЬ» НЕ БЛОКИРУЕМ.
//    На согласованном макете было «до ответа на вопрос кнопка
//    неактивна». На практике это отнимало у клиента возможность купить:
//    он уже положил макет из каталога, но обязан нажать радиокнопку
//    ради галочки. Поэтому вариант ① выбран ПО УМОЛЧАНИЮ, если в
//    корзине есть позиции из каталога; иначе по умолчанию — ③, но
//    оформление всё равно доступно, а «макет ждём» честно записывается.
// ═══════════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  uploadStickerCartImage,
  updateStickerCartItem,
} from '../../../store/stickerCartSlice.js';
import {
  validateStickerImage,
  checkLightEdges,
  formatSize,
  STICKER_IMAGE_ACCEPT,
} from '../../../utils/imageUpload.js';

const Option = ({ checked, onSelect, title, hint, img, children, badge }) => (
  <div
    onClick={onSelect}
    role="radio"
    aria-checked={checked}
    tabIndex={0}
    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') onSelect(); }}
    style={{
      display: 'flex',
      gap: 12,
      alignItems: 'center',
      border: `1px solid ${checked ? 'rgb(var(--c-accent))' : 'rgb(var(--c-border))'}`,
      background: checked ? 'rgb(var(--c-accent-soft))' : 'rgb(var(--c-surface))',
      borderRadius: 12,
      padding: 12,
      marginTop: 8,
      cursor: 'pointer',
    }}
  >
    <span
      style={{
        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
        border: checked ? '7px solid rgb(var(--c-accent))' : '1.5px solid rgb(var(--c-border))',
        background: '#fff',
        boxSizing: 'border-box',
      }}
    />
    <div style={{ flex: 1, minWidth: 0 }}>
      <div style={{ fontSize: 13.5, fontWeight: checked ? 700 : 600, color: 'rgb(var(--c-text))' }}>
        {title}
      </div>
      {hint && <div style={{ fontSize: 12, color: 'rgb(var(--c-text-secondary))', marginTop: 2 }}>{hint}</div>}
      {badge && <div style={{ fontSize: 11.5, color: 'rgb(var(--c-text-secondary))', marginTop: 4 }}>{badge}</div>}
    </div>
    {img && (
      <img src={img} alt="" style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, flexShrink: 0 }} />
    )}
    {children}
  </div>
);

/**
 * @param {object} props
 * @param {Array} props.items — строки корзины стикеров (state.stickerCart.items)
 * @param {(mode:'catalog'|'upload'|'pending')=>void} props.onModeChange — выбранный вариант
 * @param {'catalog'|'upload'|'pending'} props.mode
 */
export const StickerDesignStep = ({ items, mode, onModeChange }) => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  // Цель загрузки: если в корзине одна позиция — грузим в неё, если
  // несколько — клиент указывает, к какой относится файл.
  const [targetId, setTargetId] = useState(items[0]?.id || null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [warning, setWarning] = useState(null);

  useEffect(() => {
    if (!items.find((i) => i.id === targetId)) setTargetId(items[0]?.id || null);
  }, [items, targetId]);

  const target = items.find((i) => i.id === targetId) || null;

  const pickFile = async (file) => {
    if (!file) return;
    setError('');
    setWarning(null);
    const check = validateStickerImage(file);
    if (!check.ok) {
      setError(check.error);
      if (inputRef.current) inputRef.current.value = '';
      return;
    }
    if (!target) {
      setError('Не выбрана позиция, к которой относится картинка');
      return;
    }
    const warn = await checkLightEdges(file);
    setWarning(warn);

    setBusy(true);
    setProgress(0);
    try {
      await dispatch(uploadStickerCartImage({ id: target.id, file, onProgress: setProgress })).unwrap();
      // После загрузки позиция перестаёт быть «макет ждём».
      await dispatch(updateStickerCartItem({ id: target.id, data: { rights_confirmed: true } })).unwrap();
      onModeChange('catalog'); // файлы приложены — «печатать можно» по всем
    } catch (e) {
      setError(typeof e === 'string' ? e : (e?.message || 'Не удалось загрузить картинку'));
    } finally {
      setBusy(false);
    }
  };

  const catalogItems = items.filter((i) => i.kind === 'catalog');
  const firstCatalog = catalogItems[0] || null;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 4 }}>
        <span
          style={{
            width: 22, height: 22, borderRadius: '50%',
            background: 'rgb(var(--c-accent))', color: '#fff',
            display: 'grid', placeItems: 'center', fontSize: 12, fontWeight: 700,
          }}
        >
          2
        </span>
        <b style={{ fontSize: 15, color: 'rgb(var(--c-text))' }}>Какие стикеры напечатать?</b>
      </div>
      <div style={{ fontSize: 12, color: 'rgb(var(--c-text-secondary))', marginBottom: 4 }}>
        Это нужно, чтобы мы знали, что печатать. Можно оформить без картинки — тогда заказ
        уйдёт как «макет ждём».
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={STICKER_IMAGE_ACCEPT}
        style={{ display: 'none' }}
        onChange={(e) => pickFile(e.target.files?.[0])}
      />

      {/* ① КАТАЛОГ */}
      <Option
        checked={mode === 'catalog'}
        onSelect={() => onModeChange('catalog')}
        title="Возьму макет из каталога"
        hint={firstCatalog ? `«${firstCatalog.title}» · ${Math.round(Number(firstCatalog.size_mm) / 10)} см` : 'В корзине нет макета из каталога'}
        img={firstCatalog?.preview || null}
      />

      {/* ② СВОЯ КАРТИНКА */}
      <Option
        checked={mode === 'upload'}
        onSelect={() => {
          onModeChange('upload');
          // 🔑 СРАЗУ ОТКРЫВАЕМ ВЫБОР ФАЙЛА: человек нажал «загружу» — значит,
          //    следующий шаг очевиден, лишняя кнопка только тормозит.
          setTimeout(() => inputRef.current?.click(), 0);
        }}
        title="Загружу свою картинку"
        hint="PNG, JPG, WEBP, TIFF · до 20 МБ"
        badge={target?.design_status === 'uploaded' ? `Приложена: ${target.custom_original_name || 'картинка'}` : null}
      >
        {items.length > 1 && (
          <select
            value={targetId || ''}
            onChange={(e) => setTargetId(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{ fontSize: 12, padding: '6px 8px', borderRadius: 8, border: '1px solid rgb(var(--c-border))' }}
          >
            {items.map((i) => (
              <option key={i.id} value={i.id}>
                {i.title} · {Math.round(Number(i.size_mm) / 10)} см
              </option>
            ))}
          </select>
        )}
      </Option>

      {/* ③ МАКЕТ ЖДЁМ */}
      <Option
        checked={mode === 'pending'}
        onSelect={() => onModeChange('pending')}
        title="Ещё не решил — помогите подобрать"
        hint="Заказ оформится, печать начнётся, когда пришлёте картинку. Свяжемся и подскажем."
      />

      {busy && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'rgb(var(--c-text-secondary))' }}>
          Загрузка… {progress}%
        </div>
      )}
      {error && (
        <div style={{ marginTop: 10, fontSize: 12, color: 'rgb(var(--c-danger))', lineHeight: 1.45 }}>{error}</div>
      )}
      {warning && (
        <div
          style={{
            marginTop: 10, padding: '8px 10px', borderRadius: 8,
            background: 'rgb(var(--c-accent-soft))',
            color: 'rgb(var(--c-text-secondary))', fontSize: 11.5, lineHeight: 1.45,
          }}
        >
          {warning}
        </div>
      )}

      <div style={{ marginTop: 12, fontSize: 11.5, color: 'rgb(var(--c-text-muted))', lineHeight: 1.5 }}>
        Белой краски нет: белые детали картинки на белом фоне не напечатаются.
        Загружая картинку, вы подтверждаете, что права на изображение у вас есть.
      </div>
    </div>
  );
};

export default StickerDesignStep;
